import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  PenLine, BookOpen, Trophy, MessageCircle, Users, Star, 
  ArrowRight, Clock, Eye, Heart, Megaphone, Pin, Newspaper
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import WelcomeTour from '../components/WelcomeTour';
import StreakWidget from '../components/StreakWidget';
import WritingGoals from '../components/WritingGoals';
import ActivityFeed from '../components/ActivityFeed';
import RecentlyUpdated from '../components/RecentlyUpdated';

const DashboardPage = () => {
  const { user, api } = useAuth();
  const [stats, setStats] = useState(null);
  const [myStories, setMyStories] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [spotlight, setSpotlight] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTour, setShowTour] = useState(false);
  const [collabInvites, setCollabInvites] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [latestWorks, setLatestWorks] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [storiesRes, challengesRes, spotlightRes, invitesRes, announcementsRes, latestRes] = await Promise.allSettled([
          api.get('/stories/my?include_drafts=true'),
          api.get('/challenges?status=active'),
          api.get('/spotlight/current'),
          api.get('/collaborations/invites'),
          api.get('/announcements?limit=3'),
          api.get('/stories?limit=6')
        ]);
        
        if (storiesRes.status === 'fulfilled') {
          setMyStories(storiesRes.value.data.slice(0, 3));
          const drafts = storiesRes.value.data.filter(s => s.is_draft).length;
          const published = storiesRes.value.data.filter(s => !s.is_draft).length;
          setStats({ drafts, published, total: storiesRes.value.data.length });
        } else if (user?.id) {
          // Fallback: fetch this user's published works via public endpoint
          try {
            const fallbackRes = await api.get(`/stories?author_id=${user.id}&include_drafts=true`);
            setMyStories(fallbackRes.data.slice(0, 3));
            const drafts = fallbackRes.data.filter(s => s.is_draft).length;
            const published = fallbackRes.data.filter(s => !s.is_draft).length;
            setStats({ drafts, published, total: fallbackRes.data.length });
          } catch {
            setStats({ drafts: 0, published: 0, total: 0 });
          }
        }
        if (challengesRes.status === 'fulfilled') setChallenges(challengesRes.value.data.slice(0, 2));
        if (spotlightRes.status === 'fulfilled') setSpotlight(spotlightRes.value.data);
        if (invitesRes.status === 'fulfilled') setCollabInvites(invitesRes.value.data);
        if (announcementsRes.status === 'fulfilled') setAnnouncements(announcementsRes.value.data);
        if (latestRes.status === 'fulfilled') setLatestWorks(latestRes.value.data.filter(s => s.author_id !== user?.id).slice(0, 6));
      } catch (error) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [api, user?.id]);

  useEffect(() => {
    if (!loading && user && !user.tour_completed) {
      setShowTour(true);
    }
  }, [loading, user]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Skeleton className="h-12 w-64 mb-8" />
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="dashboard-page">
      {/* Welcome Tour */}
      {showTour && <WelcomeTour onComplete={() => setShowTour(false)} />}

      {/* Collaboration Invites */}
      {collabInvites.length > 0 && (
        <div className="mb-6 p-4 bg-white rounded-xl border border-brand-accent/30" data-testid="collab-invites">
          <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-brand-accent" />
            Collaboration Invitations
          </h3>
          <div className="space-y-3">
            {collabInvites.map(inv => (
              <div key={inv.id} className="flex items-center justify-between gap-3 p-3 bg-surface-alt rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary">
                    <strong>{inv.inviter_name}</strong> invited you to collaborate on <strong>"{inv.story_title}"</strong>
                  </p>
                  {inv.message && <p className="text-xs text-text-muted mt-1">{inv.message}</p>}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    className="btn-accent text-xs px-3 py-1"
                    onClick={async () => {
                      await api.post(`/collaborations/invites/${inv.id}/accept`);
                      setCollabInvites(collabInvites.filter(i => i.id !== inv.id));
                    }}
                    data-testid={`accept-invite-${inv.id}`}
                  >
                    Accept
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs px-3 py-1"
                    onClick={async () => {
                      await api.post(`/collaborations/invites/${inv.id}/decline`);
                      setCollabInvites(collabInvites.filter(i => i.id !== inv.id));
                    }}
                    data-testid={`decline-invite-${inv.id}`}
                  >
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Community Board Banner */}
      {announcements.length > 0 && (
        <div className="mb-8" data-testid="community-board-banner">
          <div className="bg-gradient-to-r from-[#1A3A52] to-[#2A4A62] rounded-xl overflow-hidden shadow-lg">
            <div className="px-6 py-4 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-[#C67B5C]" />
                </div>
                <h3 className="font-serif text-lg font-semibold text-white">Community Board</h3>
              </div>
              <Link to="/community-board" className="text-sm text-[#C67B5C] hover:text-[#D88B6C] font-medium flex items-center gap-1 transition-colors" data-testid="view-all-announcements">
                View all <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="divide-y divide-white/10">
              {announcements.slice(0, 2).map((ann) => (
                <Link key={ann.id} to="/community-board" className="block px-6 py-4 hover:bg-white/5 transition-colors" data-testid={`announcement-${ann.id}`}>
                  <div className="flex items-start gap-3">
                    {ann.pinned && <Pin className="w-4 h-4 text-[#C67B5C] flex-shrink-0 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-white text-sm mb-1">{ann.title}</h4>
                      <p className="text-white/60 text-xs line-clamp-2">{ann.content}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-white/40 text-xs">
                          {ann.author_name} · {new Date(ann.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-text-primary mb-2">
            Welcome back, {user?.display_name || user?.username}
          </h1>
          <p className="text-text-secondary">Ready to write something amazing today?</p>
        </div>
        <Link to="/write" data-testid="dashboard-write-button">
          <Button className="btn-accent gap-2">
            <PenLine className="w-4 h-4" />
            Start Writing
          </Button>
        </Link>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <Card className="bg-white border-brand-primary/10" data-testid="stat-published">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-text-primary">{stats?.published || 0}</p>
                <p className="text-sm text-text-muted">Published</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-brand-primary/10" data-testid="stat-drafts">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <PenLine className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-text-primary">{stats?.drafts || 0}</p>
                <p className="text-sm text-text-muted">Drafts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-brand-primary/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-brand-accent/10 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-brand-accent" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-text-primary">{challenges.length}</p>
                <p className="text-sm text-text-muted">Active Challenges</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-brand-primary/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <Link to="/groups/my" className="text-2xl font-semibold text-text-primary hover:text-brand-accent">
                  View
                </Link>
                <p className="text-sm text-text-muted">My Groups</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* My Recent Stories */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-xl font-semibold text-text-primary">My Recent Stories</h2>
            <Link to="/my-stories" className="text-brand-accent hover:underline text-sm font-medium flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          {myStories.length === 0 ? (
            <Card className="bg-white border-brand-primary/10">
              <CardContent className="p-12 text-center">
                <PenLine className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="font-serif text-lg font-medium text-text-primary mb-2">No stories yet</h3>
                <p className="text-text-secondary mb-4">Start your writing journey today!</p>
                <Link to="/write">
                  <Button className="btn-primary">Write Your First Story</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {myStories.map((story) => (
                <Link 
                  key={story.id} 
                  to={`/story/${story.id}/edit`}
                  className="block"
                  data-testid={`story-card-${story.id}`}
                >
                  <Card className="bg-white border-brand-primary/10 card-hover">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {story.is_draft && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                Draft
                              </span>
                            )}
                            {story.challenge_hidden && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700" data-testid={`hidden-badge-${story.id}`}>
                                Hidden (Blind Judging)
                              </span>
                            )}
                            <span className="text-xs text-text-muted">{story.genre}</span>
                            {(story.content?.match(/\[chapter:/g) || []).length > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">
                                {(story.content.match(/\[chapter:/g) || []).length} ch.
                              </span>
                            )}
                          </div>
                          <h3 className="font-serif text-lg font-medium text-text-primary mb-1">
                            {story.title}
                          </h3>
                          <p className="text-sm text-text-secondary line-clamp-2">
                            {story.summary || 'No summary yet...'}
                          </p>
                          <div className="flex items-center gap-4 mt-3 text-sm text-text-muted">
                            <span className="flex items-center gap-1">
                              <Eye className="w-4 h-4" /> {story.view_count}
                            </span>
                            <span className="flex items-center gap-1">
                              <Heart className="w-4 h-4" /> {story.like_count}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {new Date(story.updated_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        {story.cover_image && (
                          <img 
                            src={story.cover_image} 
                            alt={story.title}
                            className="w-20 h-20 rounded-lg object-cover"
                          />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Activity Feed (below My Stories) */}
        <div className="lg:col-span-2">
          {/* Latest from the Community */}
          {latestWorks.length > 0 && (
            <div className="mb-8" data-testid="latest-works-section">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-serif text-xl font-semibold text-text-primary flex items-center gap-2">
                  <Newspaper className="w-5 h-5 text-brand-accent" />
                  Latest from the Community
                </h2>
                <Link to="/stories" className="text-brand-accent hover:underline text-sm font-medium flex items-center gap-1">
                  Browse all <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {latestWorks.map((work) => (
                  <Link key={work.id} to={`/story/${work.id}`} className="block" data-testid={`latest-work-${work.id}`}>
                    <Card className="bg-white border-brand-primary/10 card-hover h-full">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          {work.cover_image ? (
                            <img src={work.cover_image} alt={work.title} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-14 h-14 rounded-lg bg-brand-primary/5 flex items-center justify-center flex-shrink-0">
                              <BookOpen className="w-6 h-6 text-brand-primary/30" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-brand-accent font-medium">{work.genre}</span>
                              {(work.content?.match(/\[chapter:/g) || []).length > 0 && (
                                <span className="text-xs text-blue-500">{(work.content.match(/\[chapter:/g) || []).length} ch.</span>
                              )}
                            </div>
                            <h4 className="font-serif font-medium text-text-primary text-sm leading-snug line-clamp-1">{work.title}</h4>
                            <p className="text-xs text-text-muted mt-1">by {work.author_name}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-text-muted">
                              <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" /> {work.view_count}</span>
                              <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" /> {work.like_count}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <ActivityFeed />
        </div>

        {/* Sidebar */}
        <div className="space-y-8">
          {/* Writing Goals */}
          <WritingGoals />

          {/* Recently Updated */}
          <RecentlyUpdated />

          {/* Writing Streak */}
          <div>
            <h2 className="font-serif text-lg font-semibold text-text-primary mb-4">Writing Streak</h2>
            <StreakWidget />
          </div>

          {/* Active Challenges */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-lg font-semibold text-text-primary">Active Challenges</h2>
              <Link to="/challenges" className="text-brand-accent hover:underline text-sm">View all</Link>
            </div>
            
            {challenges.length === 0 ? (
              <Card className="bg-white border-brand-primary/10">
                <CardContent className="p-6 text-center">
                  <Trophy className="w-8 h-8 text-text-muted mx-auto mb-2" />
                  <p className="text-sm text-text-secondary">No active challenges right now</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {challenges.map((challenge) => (
                  <Link key={challenge.id} to={`/challenges/${challenge.id}`}>
                    <Card className="bg-white border-brand-primary/10 card-hover">
                      <CardContent className="p-4">
                        <h3 className="font-medium text-text-primary mb-1">{challenge.title}</h3>
                        <p className="text-xs text-text-muted">
                          Ends {new Date(challenge.end_date).toLocaleDateString()}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Spotlight Works */}
          {spotlight.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-brand-tertiary" />
                <h2 className="font-serif text-lg font-semibold text-text-primary">This Week's Spotlight</h2>
              </div>
              <div className="space-y-3">
                {spotlight.map((item) => (
                  <Link key={item.id} to={`/story/${item.story_id}`}>
                    <Card className="bg-gradient-to-br from-brand-tertiary/10 to-brand-accent/5 border-brand-tertiary/20 card-hover">
                      <CardContent className="p-4">
                        <p className="text-xs text-brand-accent font-medium mb-1">
                          Selected by {item.selector_name}
                        </p>
                        <h3 className="font-medium text-text-primary">{item.story_title}</h3>
                        <p className="text-sm text-text-secondary">by {item.story_author_name}</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Quick Links */}
          <div>
            <h2 className="font-serif text-lg font-semibold text-text-primary mb-4">Quick Links</h2>
            <div className="space-y-2">
              <Link to="/reading" className="flex items-center gap-3 p-3 rounded-lg hover:bg-brand-primary/5 transition-colors">
                <BookOpen className="w-5 h-5 text-brand-accent" />
                <span className="text-text-secondary">What Are You Reading?</span>
              </Link>
              <Link to="/guest-authors" className="flex items-center gap-3 p-3 rounded-lg hover:bg-brand-primary/5 transition-colors">
                <Star className="w-5 h-5 text-brand-accent" />
                <span className="text-text-secondary">Guest Authors</span>
              </Link>
              <Link to="/groups" className="flex items-center gap-3 p-3 rounded-lg hover:bg-brand-primary/5 transition-colors">
                <Users className="w-5 h-5 text-brand-accent" />
                <span className="text-text-secondary">Writing Groups</span>
              </Link>
              <Link to="/messages" className="flex items-center gap-3 p-3 rounded-lg hover:bg-brand-primary/5 transition-colors">
                <MessageCircle className="w-5 h-5 text-brand-accent" />
                <span className="text-text-secondary">Messages</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
